/**
 * RBAC: role for UI (menu visibility, button enable/disable).
 * Reads from localStorage key `saa_role` (set by login or admin).
 * Backend enforces roles on API; this is for UI only.
 */
export type AppRole = 'free' | 'standard' | 'pro' | 'institutional' | 'client' | 'viewer' | 'analyst' | 'approver' | 'pm' | 'admin';

const STORAGE_KEY = 'saa_role';

// 'free' is the DEFAULT tier: an unauthenticated visitor gets the free experience (Analysis only,
// top-10 assets, no report/portfolio/desk/alerts). Any higher role (set via Admin or localStorage
// saa_role) unlocks the full product — the interim access model until real auth/registration ships.
export function getRole(): AppRole {
  if (typeof window === 'undefined') return 'free';
  const v = window.localStorage.getItem(STORAGE_KEY);
  if (v === 'free' || v === 'standard' || v === 'pro' || v === 'institutional' || v === 'client' || v === 'viewer' || v === 'analyst' || v === 'approver' || v === 'pm' || v === 'admin') return v;
  return 'free';
}

/** True for the free tier — the gated default. Full-access roles (viewer+ and above) return false. */
export function isFreeTier(role: AppRole): boolean {
  return role === 'free';
}

export function setRole(role: AppRole): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, role);
}

/** True if role can access operations (SLO, Scenarios, Lineage, Risk Kernel, etc.). The mass-market
 *  tiers (free, and the paid `client` subscription) cannot — they see the product, not the engine. */
export function canAccessOperations(role: AppRole): boolean {
  // Commercial tiers (standard / pro / institutional, and the legacy client) are customers: product only.
  return role !== 'viewer' && role !== 'free' && role !== 'client' && role !== 'standard' && role !== 'pro' && role !== 'institutional';
}

/** True for the mass-market subscriber (paid `client` plan): the simple report product, no internals. */
export function isClient(role: AppRole): boolean {
  return role === 'client';
}

/** True only for the admin role. Used to keep internal/SRE surfaces (Operational: cost per run,
 *  burn rate, broker queues) out of every client-facing role — not just 'viewer' but 'analyst'
 *  and the other institutional roles too. */
export function isAdmin(role: AppRole): boolean {
  return role === 'admin';
}

const TOKEN_KEY = 'saa_token';

/** JWT token for API auth (set by login). */
export function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(TOKEN_KEY, token);
}

const REFRESH_KEY = 'saa_refresh';
const AUTH_CACHE_KEY = 'saa_auth_cache';

/** Last-known {role,tier,entitlements} from /api/auth/me, cached so a refresh renders the correct
 *  nav/gating INSTANTLY instead of flashing the free/locked state until /me returns. UI-only — the
 *  backend still enforces every entitlement, so a tampered cache unlocks nothing real. */
export interface AuthCache { role: AppRole; tier: string; entitlements: string[] }
export function getAuthCache(): AuthCache | null {
  if (typeof window === 'undefined') return null;
  try {
    const s = window.localStorage.getItem(AUTH_CACHE_KEY);
    if (!s) return null;
    const d = JSON.parse(s);
    return { role: (d.role || 'free') as AppRole, tier: d.tier || 'free', entitlements: Array.isArray(d.entitlements) ? d.entitlements : [] };
  } catch { return null; }
}
export function setAuthCache(v: AuthCache): void {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(v)); } catch { /* storage may be unavailable */ }
}

/** Long-lived refresh token (Phase 3) — exchanged at /api/auth/refresh for a fresh access token. */
export function getRefresh(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(REFRESH_KEY);
}

export function setRefresh(token: string): void {
  if (typeof window === 'undefined' || !token) return;
  window.localStorage.setItem(REFRESH_KEY, token);
}

/** Clear all client auth state (access + refresh + role) — used on logout and on refresh failure. */
export function clearAuth(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(TOKEN_KEY);
  window.localStorage.removeItem(REFRESH_KEY);
  window.localStorage.removeItem(STORAGE_KEY);
  window.localStorage.removeItem(AUTH_CACHE_KEY);
  // Per-account analysis cache must never survive a sign-out (another account may sign in in this tab).
  try { window.sessionStorage.removeItem('saa_last_analysis_v1'); window.sessionStorage.removeItem('saa_selected_asset'); } catch { /* ignore */ }
}

/** Headers for authenticated API requests. */
export function authHeaders(): Record<string, string> {
  const t = getToken();
  if (!t) return {};
  return { Authorization: `Bearer ${t}` };
}
